import { Mesh, Object3D } from "three";
import { _flagsMap, internalGetNode, HierarchyNode } from "../../hierarchy/common/hierarchyService.js";
import { IHierarchyNode, ITreeProvider } from "../../hierarchy/common/hierarchy.js";
import { isBatchedMeshlike, isCamera, isInstancedMeshLike, isMaterial, isMeshLike, isObject3D, isRawShaderMaterial, isRenderer, isScene, isShaderMaterial } from "./helper.js";
import { ILogService } from "../../log/common/log.js";
import { IDisposable, toDisposable } from "../../../base/common/lifecycle.js";
import { Attachment } from "../../../base/common/attachment.js";
import { isGltfLike } from "../common/gltf.js";
import { IntervalTimer } from "../../../base/common/async.js";
import { ServicesAccessor } from "../../instantiation/common/instantiation.js";
import { ActivityAttachment } from "../../hierarchy/common/attachments.js";
import { CommandService } from "../../commands/common/commandService.js";
import { SetPropertyCommand } from "../../properties/common/propertyCommands.js";
import { Needle } from "./propertyProvider.js";
import { IResourceService } from "../../resources/common/resources.js";



declare module "three" {
    interface Object3D {
        __node?: HierarchyNode;
    }
}

function detectChildHierarchyChanged(object: import("three").Object3D, callback: () => void, logger: ILogService): IDisposable {

    let lastChildIdHash = getHash();
    const i = setInterval(() => {
        const currentChildIdHash = getHash();
        if (currentChildIdHash !== lastChildIdHash) {
            // logger.debug("Child hierarchy changed " + object.name + ": " + lastChildIdHash + " -> " + currentChildIdHash);
            lastChildIdHash = currentChildIdHash;
            callback();
        }
    }, 1000);

    return toDisposable(() => {
        clearInterval(i);
    });

    function getHash(): string {
        let hash = 0;
        for (const child of object.children) {
            hash = ((hash << 5) - hash) + child.id;
            hash |= 0; // Convert to 32bit integer
        }
        return hash.toString();
    }
}

export class ThreeTreeProvider implements ITreeProvider {

    constructor(
        @ILogService private readonly logger: ILogService,
        @IResourceService private readonly resourceService: IResourceService,
    ) {
    }

    canParse(object: unknown): boolean {
        if (!object) return false;
        if (isObject3D(object)) return true;
        if (isRenderer(object)) return true;
        if (isCamera(object)) return true;
        if (isGltfLike(object)) return true;
        return false;
    }

    async createTree(accessor: ServicesAccessor, object: unknown) {

        if (isGltfLike(object)) {
            object = object.scene || object.scenes[0];
        }


        // TODO: move this into a provider syystem
        // export async function createTree(project: IProjectService, object: import("three").Object3D): Promise<Tree> {

        const logger = this.logger;

        if (isRenderer(object)) {
            const node = new HierarchyNode({
                object: object,
                id: Math.random().toString(36).substring(2, 9),
                type: "renderer",
                name: object.isWebGLRenderer ? "WebGLRenderer" : "WebGPURenderer",
                parent: null,
                expanded: true,
            });
            node.attachments.push(new ActivityAttachment(node));
            return node;
        }
        // else if (isCamera(object)) {
        //     const node = createNode(object as unknown as Object3D, null);
        //     return node;
        // }
        else if (isObject3D(object)) {
            const root = createNode(object as Object3D, null);
            for (const child of object.children) {
                traverse(child as Object3D, root, 1);
            }
            const disposable = detectChildHierarchyChanged(object, () => {
                for (const child of object.children) {
                    traverse(child as Object3D, root, 99);
                }
            }, logger);
            root.onDidDispose(() => {
                disposable.dispose();
            });
            return root;
        }

        this.logger.error("ThreeTreeProvider: Unsupported object type for creating hierarchy tree");
        return null;


        function traverse(object: Object3D, parentNode: HierarchyNode, level: number) {
            const node = createNode(object, parentNode);
            parentNode.addChild(node);
            // await new Promise(resolve => setTimeout(resolve, 1000)); // yield to avoid blocking UI
            for (const child of object.children) {
                traverse(child as Object3D, node, level + 1);
            }
        }

        function shouldAutoExpand(object: Object3D): boolean {
            // check if the object is more than 3 level deep
            let level = 0;
            let current: Object3D | null = object;
            while (current?.parent) {
                level++;
                current = current.parent as Object3D;
            }
            return level < 2;
        }

        function createNode(object: Object3D, parent: HierarchyNode | null = null, expanded?: boolean) {
            object.userData ??= {};
            let _node = internalGetNode(object);


            if (!_node) {
                if (expanded === undefined) expanded = shouldAutoExpand(object);

                _node = new HierarchyNode({
                    object: object,
                    id: object.id.toString(),
                    type: object.type,
                    name: object.name || object.type,
                    parent: parent,
                    expanded: expanded,
                });
                _node.searchTags?.push(object.type);

                if ((object as any).isBatchedMesh) {
                    _node.icon = "batchedMesh";
                    _node.searchTags?.push("batchedMesh");
                }
                else if ((object as any).isInstancedMesh) {
                    _node.icon = "batchedMesh";
                    _node.searchTags?.push("instancedMesh");
                }

                const node = _node;

                // Listen to three.js events to update the hierarchy
                object.addEventListener("childadded", evt => {
                    const child = evt.child as import("three").Object3D;
                    // logger.debug("Child added:", `${evt.target.name || object.type}/${child.name}`);
                    const node_def = createNode(child, node);
                    node.addChild(node_def);
                    for (const _child of child.children) {
                        traverse(_child as Object3D, node_def, 99);
                    }
                });
                object.addEventListener("added", (evt) => {
                    const parentNode = createNode(object.parent as Object3D);
                    parentNode.addChild(node);
                })
                object.addEventListener("removed", _ => {
                    // logger.debug("Object removed:", `${object.name || object.type}`);
                    const parent = node!.parent;
                    parent?.removeChild(node!);
                });

                const disposable = detectChildHierarchyChanged(object, () => {
                    if (_node?.parent) traverse(object, _node?.parent, 99);
                }, logger);
                _node.onDidDispose(() => {
                    disposable.dispose();
                });


                // Hack to detect object's visibility changes
                let wasVisible = object.visible;
                const interval = setInterval(() => {
                    if (node.isDisposed) {
                        clearInterval(interval);
                        return;
                    }
                    if (object.visible !== wasVisible) {
                        wasVisible = object.visible;
                        node.notifyChanged();
                    }

                    updateName();

                }, 300);

                const meshLike = isMeshLike(object);
                function updateName() {
                    node.name = object.name;

                    let newLabel = object.name || object.type;
                    // if the object does not have a unique name we append the material name (if any)
                    if (!object.name) {
                        let namePostfix = "";
                        if (meshLike && "material" in object) {
                            const material = object["material"];
                            if (isMaterial(material) && material.name) {
                                let materialName = material.name;
                                namePostfix = ` (${materialName})`;
                            }
                        }
                        else if (isScene(object)) {
                            if (object.children.length === 1 && object.children[0].children?.length === 0) {
                                const onlyChild = object.children[0];
                                if (isMeshLike(onlyChild) && "material" in onlyChild) {
                                    if (isShaderMaterial(onlyChild.material) || isRawShaderMaterial(onlyChild.material)) {
                                        namePostfix = ` (Postprocessing)`;
                                    }
                                }
                            }
                        }
                        if (namePostfix.length === 0) {
                            namePostfix = `_${object.id}`;
                        }

                        newLabel += namePostfix;
                    }

                    node.label = newLabel;
                }

                node.attachments.push(new VisibilityAttachment(node, object));

                if (meshLike) {
                    node.attachments.push(new InstancingInfoAttachment(node, object as Mesh));
                    node.attachments.push(new HeavyAttachment(node, object));
                }

                if (Needle.objectHasComponents(object)?.hasComponents) {
                    node.attachments.push(new HasComponentsAttachment(node, object));
                }

                if (isCamera(object) || isScene(object)) {
                    _node.attachments.push(new ActivityAttachment(_node));
                }


                // Delayed parenting check. If a node has been removed in the inspector from the parent (and still has no parent)
                // Then we want to sync this to the three.js hierarchy by removing the three.js object from it's hierarchy.
                // Maybe/ideally this shouldnt be done like this - not exactly sure why we have to even do this BECAUSE we're tracking three.js remove events above
                // And removing the node in the inspector hierarchy should directly trigger three.js remove events
                let parentingCheckIsScheduled = false;
                const checkParenting = () => {
                    if (!parentingCheckIsScheduled) return;
                    parentingCheckIsScheduled = false;
                    // Remove the object3d from parent if the hierarchy node has no parent
                    if (!node?.parent) {
                        if (object.removeFromParent) {
                            object.removeFromParent();
                        }
                        else {
                            const parent = object.parent;
                            if (parent) parent.remove(object);
                            else logger.warn(`Object has no parent to remove from: ${object.name || "unnamed"} (${object.type})`);
                        }
                        logger.debug("Removed object from parent, type:", object.type);
                    }
                    // Add the object3d to the correct parent if needed
                    else {
                        const obj = node!.owner as import("three").Object3D;
                        const parent = node!.parent?.owner as import("three").Object3D;
                        if (obj.parent !== parent) {
                            parent.add(obj);
                            // make sure index in three.js matches
                            const currentIndex = parent.children.indexOf(obj);
                            const desiredIndex = parent.children.indexOf((node!.parent as HierarchyNode).owner as import("three").Object3D);
                            if (currentIndex !== desiredIndex) {
                                parent.children.splice(currentIndex, 1);
                                parent.children.splice(desiredIndex, 0, obj);
                            }
                        }
                    }
                }

                // Listen to node events to update three.js object
                // IMPORTANT: We do this at the end because we don't want to receive did change events on the node triggered by any of the above code (e.g. setting the flags in ActivityAttachment triggers the CubeCamera to be removed: https://bruno-simon.com/)
                node.onDidChange(({ node: _node }) => {
                    if (_node === node) {
                        if (!parentingCheckIsScheduled) {
                            // sync parenting on next tick
                            parentingCheckIsScheduled = true;
                            setTimeout(checkParenting, 0);
                        }
                    }

                    // HACK: sync node name
                    updateName();
                });


            }

            return _node;
        }
    }
}


class VisibilityAttachment extends Attachment {

    constructor(node: IHierarchyNode, object: Object3D) {

        const visibleIcon = "visible";
        const hiddenIcon = "invisible";

        super(
            "visibility",
            undefined,
            "Click to toggle visibility",
            node.visible ? visibleIcon : hiddenIcon,
            () => {
                this._name = (object.name || object.type) + "_" + object.id;
                CommandService.get().executeCommand<SetPropertyCommand>(SetPropertyCommand.ID, {
                    objectId: node.id,
                    propertyPath: "visible",
                    value: !object.visible,
                    previousValue: object.visible,
                });
            }
        )

        this._name = (object.name || object.type) + "_" + object.id;

        this._register(node.onDidChange(() => {
            this.icon = node.visible ? visibleIcon : hiddenIcon;
        }));
    }
}

class HeavyAttachment extends Attachment {

    constructor(node: IHierarchyNode, object: Mesh) {
        super(
            "heavy",
            undefined,
            `Mesh with more than 10,000 vertices`,
            "icon-heavy",
        );
        this._name = (object.name || object.type) + "_" + object.id;
        const threshold = 10_000;
        this.visible = object.geometry?.attributes?.position?.count! > threshold;
        this._register(node.onDidChange(() => {
            const geometry = object.geometry;
            const vertices = geometry?.attributes?.position?.count || 0;
            this.description = `This mesh has ${vertices?.toLocaleString()} vertices`;
            this.visible = vertices > threshold;
        }));
        const i = setInterval(() => {
            const geometry = object.geometry;
            const vertices = geometry?.attributes?.position?.count || 0;
            this.description = `This mesh has ${vertices.toLocaleString()} vertices`;
            this.visible = vertices > threshold;
        }, 1000);
        this._register(toDisposable(() => clearInterval(i)));
    }
}

class HasComponentsAttachment extends Attachment {
    constructor(node: IHierarchyNode, object: Object3D) {
        super(
            "components",
            undefined,
            `Object has components attached`,
            "component",
        );
    }
}


class InstancingInfoAttachment extends Attachment {

    constructor(node: IHierarchyNode, object: Mesh) {
        super(
            "instancingInfo",
            undefined,
            undefined,
            undefined,
        );
        this.visible = isInstancedMeshLike(object) || isBatchedMeshlike(object);
        this._register(new IntervalTimer()).cancelAndSet(() => {
            if (isInstancedMeshLike(object)) {
                this.visible = true;
                const count = object.count;
                this.label = String(count);
                this.description = `InstancedMesh with ${count} instances`;
            }
            else if (isBatchedMeshlike(object) && object.instanceCount !== undefined) {
                this.visible = true;
                this.label = object.instanceCount !== undefined ? `${object.instanceCount?.toLocaleString()}` : "";///${object.maxInstanceCount}`;
                const maxInstanceCount = object.maxInstanceCount ?? 0;
                this.description = `BatchedMesh with ${(object.instanceCount ?? "?")?.toLocaleString()} / ${maxInstanceCount?.toLocaleString()} instances (${(maxInstanceCount - (object.instanceCount ?? "?"))?.toLocaleString()} free)`;
            }
            else this.visible = false;
        }, 1000)
    }
}